import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Company, type Counterparty, type NewCounterparty } from '../api';
import type { Dictionary } from '../i18n';
import { nip as formatNip } from '../format';
import * as Icon from '../components/Icons';
import { CounterpartyFields, emptyCounterparty, useRegistryLookup } from '../components/CounterpartyDialog';

export function CounterpartiesPanel({ company, t }: { company: Company; t: Dictionary }) {
  const [items, setItems] = useState<Counterparty[] | null>(null);
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState<NewCounterparty | null>(null);
  const { fetched, setFetched, fetching, lookup } = useRegistryLookup(t);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await api.counterparties(company.id, query, includeInactive));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, query, includeInactive]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 250); // chwila zwłoki, żeby nie pytać serwera przy każdej literze
    return () => clearTimeout(timer);
  }, [load]);

  async function fetchFromRegistry() {
    if (!form) return;
    setError(null);
    const result = await lookup(form);
    if (typeof result === 'string') setError(result);
    else setForm(result);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      await api.addCounterparty(company.id, form);
      setForm(null);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.deactivateCounterparty(company.id, id);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const editable = !company.roles.every((role) => role === 'VIEWER');

  /*
   * Formularz ma własny ekran z okruszkiem, a nie wisi pod listą jako kolejny wiersz
   * bez nagłówka. Obok niego stoi zestawienie stawek, bo to rodzaj kontrahenta o nich decyduje.
   */
  if (form) {
    return (
      <>
        <div className="hd">
          <div>
            <h3>{t.counterparties.add}</h3>
            <p>{t.counterparties.lead}</p>
          </div>
        </div>

        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}

        <div className="grid aside">
          <form className="panel" onSubmit={save}>
            <CounterpartyFields
              form={form}
              onChange={(next) => {
                setForm(next);
                if (next.nip !== form.nip) setFetched(null);
              }}
              t={t}
              fetched={fetched}
              fetching={fetching}
              onFetch={() => void fetchFromRegistry()}
            />
            <div className="bar2">
              <button type="submit" className="btn" disabled={busy}>
                {busy ? t.companies.saving : t.counterparties.save}
              </button>
              <div className="sp">
                <button type="button" className="btn ghost" onClick={() => setForm(null)} disabled={busy}>
                  {t.companies.cancel}
                </button>
              </div>
            </div>
          </form>

          <div className="panel">
            <header>
              <h4>{t.counterparties.ratesTitle}</h4>
            </header>
            <dl className="brk">
              <dt>{t.counterparties.types.DOMESTIC}</dt>
              <dd>23%, 8%, 5%, 0%, zw.</dd>
              <dt>{t.counterparties.types.EU}</dt>
              <dd>np.</dd>
              <dt>{t.counterparties.types.NON_EU}</dt>
              <dd>0%</dd>
              <dt>{t.counterparties.types.PERSON}</dt>
              <dd>23%, 8%, 5%</dd>
            </dl>
            <p className="hint">{t.counterparties.ratesHint}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.counterparties.title}</h3>
          <p>{t.counterparties.lead}</p>
        </div>
        {editable && (
          <div className="sp">
            <button type="button" className="btn" onClick={() => setForm(emptyCounterparty)}>
              <Icon.Plus />
              {t.counterparties.add}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="row">
          <input
            className="inp"
            style={{ flex: '1 1 260px' }}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.counterparties.search}
            aria-label={t.counterparties.search}
          />
          <label className="check">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            {t.counterparties.showInactive}
          </label>
          <span className="sm muted" style={{ marginLeft: 'auto' }}>
            {items?.length ?? 0}
          </span>
        </div>

        {items?.length === 0 ? (
          <div className="empty">
            <Icon.Users />
            <b>{t.counterparties.none}</b>
          </div>
        ) : (
          items && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.counterparties.name}</th>
                    <th>{t.counterparties.nip}</th>
                    <th>{t.counterparties.type}</th>
                    <th>{t.counterparties.city}</th>
                    <th>{t.costs.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      {/* NIP w polskim formacie — tym samym, którym firma posługuje się w dokumentach */}
                      <td className="mono">{item.nip ? formatNip(item.nip) : (item.vatEuId ?? '—')}</td>
                      <td>{t.counterparties.types[item.type]}</td>
                      <td>{[item.city, item.country].filter(Boolean).join(', ') || '—'}</td>
                      <td>
                        <span className={item.active ? 'chip st-ok' : 'chip st-draft'}>
                          <i />
                          {item.active ? t.counterparties.active : t.counterparties.inactive}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && item.active && (
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={busy}
                              onClick={() => void deactivate(item.id)}
                            >
                              {t.counterparties.deactivate}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </>
  );
}
